import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import streamChatService from "../services/streamChatService";
import streamChatUnreadSync from "../services/streamChatUnreadSync";
import {
  initializeStreamChat,
  getChatGroupsList,
  getSupportTypes,
} from "../redux/actions/streamChatActions";
import streamChatConfig from "../settings/streamChatConfig";
import { setConnectionStatus } from "../redux/reducer/streamChatReducer";
import { App } from "@capacitor/app";
import { getAuthToken } from "../services/authStorage";

// Seed the unread store from the curated VISIBLE chats (groups + 1:1 + support)
// so the toolbar badge is correct app-wide before the Chat page is ever opened.
// The badge sums these only (see selectTotalUnread); hidden/stale chats are
// never returned here, so they don't inflate the count. Higher limit than the
// Chat page's 20 so the total isn't truncated for users with many chats.
const seedVisibleUnread = (dispatch) => {
  // The Chat page fetches these same lists for its visible sections and the
  // unread slice consumes those results. Avoid a second, larger request burst
  // when Chat itself is already responsible for loading them.
  if (window.location.pathname.startsWith("/chat")) return;

  dispatch(
    getChatGroupsList({
      criteria: { group_types_included: ["public", "regional"], is_member: true },
      page: 1,
      limit: 100,
    }),
  );
  dispatch(
    getChatGroupsList({
      criteria: { group_types_included: ["private"], is_member: true },
      page: 1,
      limit: 100,
    }),
  );
  dispatch(getSupportTypes());
};

/**
 * Keeps a Stream Chat connection alive app-wide (for the logged-in user) and
 * attaches the real-time unread listener so the toolbar chat badge updates live
 * — even when the Chat page is closed.
 *
 * Renders nothing. Connection lifecycle:
 *  - Connect when a user + auth token are present.
 *  - Reuse an already-open connection (e.g. opened by the Chat page).
 *  - Logout triggers a full page reload, which tears down the socket.
 */
const StreamChatBootstrap = () => {
  const dispatch = useDispatch();
  const userId = useSelector((s) => s.user?.user?.id);
  const connectingRef = useRef(false);
  const retryTimerRef = useRef(null);
  const retryAttemptRef = useRef(0);
  const previousConnectionStateRef = useRef("idle");

  useEffect(() => {
    return streamChatService.subscribeToConnectionState(({ state, error }) => {
      dispatch(
        setConnectionStatus({
          isConnected: state === "online",
          error: error?.message || null,
        }),
      );

      if (
        state === "online" &&
        previousConnectionStateRef.current !== "online"
      ) {
        retryAttemptRef.current = 0;
        streamChatUnreadSync.start(streamChatService.getClient());
        seedVisibleUnread(dispatch);
      }
      previousConnectionStateRef.current = state;
    });
  }, [dispatch]);

  useEffect(() => {
    const hasToken = !!getAuthToken();
    if (!userId || !hasToken) return;

    let stopped = false;
    const scheduleRetry = () => {
      if (stopped || retryTimerRef.current || navigator.onLine === false) return;
      const attempt = retryAttemptRef.current;
      const delayMs = Math.min(30000, 1000 * Math.pow(2, attempt));
      retryAttemptRef.current += 1;
      retryTimerRef.current = setTimeout(() => {
        retryTimerRef.current = null;
        connect();
      }, delayMs);
    };

    const connect = async () => {
      if (stopped || connectingRef.current) return;
      if (streamChatService.isClientConnected()) return;

      connectingRef.current = true;
      try {
        const result = await dispatch(initializeStreamChat());
        if (initializeStreamChat.rejected.match(result)) {
          throw new Error(result.payload || "Chat initialization failed");
        }

        const { userConfig, token, apiKey } = result.payload || {};
        const finalApiKey = apiKey || streamChatConfig.apiKey;
        await streamChatService.initialize(
          finalApiKey,
          userConfig,
          token,
        );
      } catch (err) {
        console.warn("StreamChatBootstrap connect failed:", err?.message || err);
        scheduleRetry();
      } finally {
        connectingRef.current = false;
      }
    };

    const handleOnline = () => {
      retryAttemptRef.current = 0;
      connect();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") connect();
    };

    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    let appStateListener = null;
    App.addListener("appStateChange", ({ isActive }) => {
      if (isActive) connect();
    })
      .then((listener) => {
        if (stopped) {
          listener.remove();
        } else {
          appStateListener = listener;
        }
      })
      .catch(() => {});
    connect();

    return () => {
      stopped = true;
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      appStateListener?.remove?.();
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = null;
      }
    };
  }, [userId, dispatch]);

  return null;
};

export default StreamChatBootstrap;
